/**
 * TenantUserSetTempPasswordModal — ADR-0022 tenant scope.
 *
 * Tenant-admin (uprawnienie users.set_temporary_password) generuje hasło
 * tymczasowe dla tenant usera. Server zwraca hasło RAZ — UI pokazuje je
 * z czerwonym ostrzeżeniem i przyciskiem "Skopiuj".
 *
 * UX dwustopniowy:
 *  1. Confirmation step — ostrzeżenie + przycisk „Wygeneruj hasło".
 *  2. Reveal step — wygenerowane hasło widoczne raz, z przyciskiem skopiowania.
 *     Zamknięcie modalu kończy reveal — hasło jest stracone.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

export interface TenantUserSetTempPasswordModalProps {
  open: boolean;
  onClose: () => void;
  user: { id: string; email: string; displayName: string | null } | null;
}

type State =
  | { kind: 'confirm' }
  | { kind: 'submitting' }
  | { kind: 'reveal'; password: string }
  | { kind: 'error'; message: string };

export function TenantUserSetTempPasswordModal({
  open,
  onClose,
  user,
}: TenantUserSetTempPasswordModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [state, setState] = useState<State>({ kind: 'confirm' });

  useEffect(() => {
    if (open) setState({ kind: 'confirm' });
  }, [open]);

  if (!user) return null;

  async function handleGenerate() {
    if (!user) return;
    setState({ kind: 'submitting' });
    try {
      const res = await api.post<{ ok: true; temporaryPassword: string }>(
        `/users/${user.id}/set-temporary-password`,
        {},
      );
      setState({ kind: 'reveal', password: res.data.temporaryPassword });
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      let msg: string;
      if (axErr.response?.status === 403) {
        msg = t('tenant.users.setTempPassword.errors.forbidden');
      } else {
        msg = t('tenant.users.setTempPassword.errors.unknown');
      }
      setState({ kind: 'error', message: msg });
    }
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.show({
        variant: 'success',
        title: t('tenant.users.setTempPassword.copied'),
      });
    } catch {
      toast.show({
        variant: 'warning',
        title: t('tenant.users.setTempPassword.copyFailed'),
      });
    }
  }

  const submitting = state.kind === 'submitting';

  let footer;
  if (state.kind === 'confirm' || state.kind === 'submitting' || state.kind === 'error') {
    footer = (
      <>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => void handleGenerate()}
          loading={submitting}
          disabled={submitting}
          data-testid="tenant-set-temp-password-generate"
        >
          {submitting
            ? t('tenant.users.setTempPassword.generating')
            : t('tenant.users.setTempPassword.generate')}
        </Button>
      </>
    );
  } else {
    footer = (
      <Button onClick={onClose} data-testid="tenant-set-temp-password-close">
        {t('tenant.users.setTempPassword.close')}
      </Button>
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title={t('tenant.users.setTempPassword.title')}
      description={t('tenant.users.setTempPassword.description', {
        name: user.displayName ?? user.email,
      })}
      size="md"
      closeOnBackdrop={state.kind !== 'reveal' && !submitting}
      closeOnEsc={state.kind !== 'reveal' && !submitting}
      footer={footer}
    >
      {state.kind === 'reveal' ? (
        <div className="space-y-4">
          <div
            role="alert"
            className="rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200"
            data-testid="tenant-set-temp-password-warning"
          >
            <p className="font-semibold">{t('tenant.users.setTempPassword.warningTitle')}</p>
            <p className="mt-1 text-xs">{t('tenant.users.setTempPassword.warningBody')}</p>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-text-secondary">
              {t('tenant.users.setTempPassword.passwordLabel')}
            </p>
            <code
              className="block rounded-lg border border-border-secondary bg-bg-secondary px-3 py-3 font-mono text-base text-text-primary"
              data-testid="tenant-set-temp-password-value"
            >
              {state.password}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void copyToClipboard(state.password)}
              data-testid="tenant-set-temp-password-copy"
            >
              {t('tenant.users.setTempPassword.copy')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p
            className="rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
            data-testid="tenant-set-temp-password-confirm"
          >
            {t('tenant.users.setTempPassword.confirmWarning')}
          </p>
          <p className="text-sm text-text-secondary">
            {t('tenant.users.setTempPassword.confirmDescription')}
          </p>
          {state.kind === 'error' && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {state.message}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
